import { RepositoryFactory } from '@/api/factory/repositoryFactory'
import unitRepository from '@/api/repository/unitRepository'
import vendor from '@/store/modules/vendor'
import utils from '@/helpers/utils.js'
import vuetify from '@/plugins/vuetify'
import dayjs from 'dayjs'
import { resolveUnitType } from '@/modules/Assignment/constants'

// i18n outside a component: this store has no `this.$vuetify`.
const t = (key, ...args) => vuetify.framework.lang.translator(key, ...args)

// Default unit name when the teacher leaves it blank: "<loại bài> ngày <DD/MM/YYYY>",
// e.g. "Bài kiểm tra ngày 21/07/2026" / "Bài tập về nhà ngày 21/07/2026".
function defaultUnitTitle(unitType) {
  const label = t(`$vuetify.COURSE_CONTENT.ASSIGNMENT.${unitType === 'homework' ? 'TYPE_BTVN' : 'TYPE_BKT'}`)
  return t('$vuetify.COURSE_CONTENT.ASSIGNMENT.DEFAULT_TITLE_PATTERN', label, dayjs().format('DD/MM/YYYY'))
}

const Assignment = RepositoryFactory.get('assignment')
const namespaced = true

const EXAM_UNIT_TYPES = ['test', 'exercise']

// Content gate for the "Xuất bản" click, before the publish dialog opens. Same rules as
// finalizeMasterForExam but side-effect free. Returns an error message, '' when valid.
async function validateExamDraft({ type, detailInfo, settings }) {
  if (!EXAM_UNIT_TYPES.includes(type)) return ''
  const questions = (detailInfo && detailInfo.questions) || []
  if (!utils.validateMark(questions, settings)) {
    return t('$vuetify.COURSE.COURSE_UNIT_RESOURCE_SELECTION.MSG_CHECK_TOTAL_MARK_AGAIN')
  }
  const qInvalid = utils.validateQuestion(questions, type)
  if (qInvalid && qInvalid.length) {
    return t('$vuetify.COURSE.COURSE_UNIT_RESOURCE_SELECTION.MSG_CHECK_MARK_AGAIN', qInvalid.join(','))
  }
  return ''
}

// Shape a fresh/list unit so draftUnit/initDraft takes its DRAFT branch (reads unit.draft
// instead of breaking data from an exam-less unit). Seeds draft.data[type] so the leaf
// editor's unguarded refresh() has a valid object.
function normalizeDraftUnit(unit, { type, title } = {}) {
  const u = { ...(unit || {}) }
  const unitType = type || u.type
  u.type = unitType
  u.title = title || u.title || u.info?.title || ''
  u.settings = { ...(u.settings || {}), isDraft: true }
  if (!u.draft || !u.draft.info) {
    u.draft = {
      info: { title: title || u.title || '', type: unitType, id: u.id },
      data: { [unitType]: { questions: [], resources: [] } },
      settings: { isDraft: true },
      dataSettings: {},
      reviewConfig: {},
      ...(u.draft || {}),
    }
  }
  return u
}

const state = {
  subjectTopicId: '',
  subjectTitle: '',
  gradeTopicId: '',
  gradeTitle: '',
  assignmentType: '',
  examFormat: '',
  title: '',
  intendedCourses: [],
  masterUnit: null,
  courses: [],
  assigned: [],
  drafts: [],
  assignedTotal: 0,
  draftTotal: 0,
  history: [],
  count: 0,
  // Create payload handed off from the dashboard dialog to the assignment page.
  pendingCreate: null,
}

const actions = {
  beginScope({ commit }, { subjectTopicId, subjectTitle, gradeTopicId, gradeTitle } = {}) {
    commit('setScope', { subjectTopicId, subjectTitle, gradeTopicId, gradeTitle })
  },

  beginCreate(
    { commit },
    { subjectTopicId, subjectTitle, gradeTopicId, gradeTitle, assignmentType, examFormat, title, intendedCourses } = {}
  ) {
    commit('setScope', { subjectTopicId, subjectTitle, gradeTopicId, gradeTitle })
    commit('setAssignmentType', assignmentType || '')
    commit('setExamFormat', examFormat || '')
    commit('setTitle', title || '')
    commit('setIntendedCourses', intendedCourses || [])
  },

  async createMasterUnit({ commit, dispatch, state }, { type, title } = {}) {
    const unitType = type || resolveUnitType(state.assignmentType, state.examFormat)
    const explicitTitle = title != null ? title : state.title
    const unitTitle = (explicitTitle && explicitTitle.trim()) || defaultUnitTitle(unitType)
    const courseIds = (state.intendedCourses || []).map((c) => c.course).filter(Boolean)
    const res = await unitRepository.create({
      isStandalone: true,
      type: unitType,
      title: unitTitle || '',
      topics: [state.subjectTopicId, state.gradeTopicId].filter(Boolean),
      info: { title: unitTitle || '', type: unitType },
      settings: { isDraft: true },
      metadata: {
        target_topics: [
          { id: state.subjectTopicId, type: 'subject' },
          { id: state.gradeTopicId, type: 'grade' },
        ],
        target_courses: courseIds.map((id) => ({ course: id, status: 'pending' })),
      },
      vendorId: vendor.state.vendor.vendorId,
    })
    const unit = normalizeDraftUnit(res.data, { type: unitType, title: unitTitle })
    commit('setMasterUnit', unit)
    await dispatch('logCreate', {
      masterUnitId: unit.id,
      title: unitTitle || unit.title || '',
      type: unitType,
    })
    return unit
  },

  async resumeDraft({ commit }, id) {
    const res = await unitRepository.fetchOne(id)
    const unit = normalizeDraftUnit(res.data || {})
    commit('setMasterUnit', unit)
    return unit
  },

  async logCreate({ state }, { masterUnitId, title, type } = {}) {
    const res = await Assignment.logCreate({
      masterUnitId,
      title,
      type,
      subjectTopic: state.subjectTopicId,
      gradeTopic: state.gradeTopicId,
    })
    return res.data
  },

  async listCourses({ commit, state }, { subjectTopic, gradeTopic } = {}) {
    const res = await Assignment.listCourses({
      subjectTopic: subjectTopic || state.subjectTopicId,
      gradeTopic: gradeTopic || state.gradeTopicId,
    })
    commit('setCourses', res.data || [])
    return res.data
  },

  // Owner-scoped classes with resolved subject/grade, for the grouped-checkbox create modal.
  async listMyClasses({ commit }) {
    const res = await Assignment.listMyClasses()
    commit('setCourses', res.data || [])
    return res.data || []
  },

  async listSections({ state }, { subjectTopic, gradeTopic } = {}) {
    const res = await Assignment.listSections({
      subjectTopic: subjectTopic || state.subjectTopicId,
      gradeTopic: gradeTopic || state.gradeTopicId,
    })
    return res.data || []
  },

  async validateDraftForAssign({ rootState, rootGetters }) {
    const type = rootState.draftUnit.type
    if (!EXAM_UNIT_TYPES.includes(type)) return
    const detailInfo = rootGetters['draftUnit/getDataUnit'] || {}
    const settings = {
      ...rootGetters['draftUnit/getUnitSetting'],
      ...rootGetters['draftUnit/getExamSetting'],
    }
    const error = await validateExamDraft({ type, detailInfo, settings })
    if (error) throw new Error(error)
  },

  // POST /assignment. Reusing an existing masterUnitId merges the new courses (no new unit).
  async assign({ state, dispatch }, { masterUnitId, courses, chapterTitle, childTitle, subjectTopic, gradeTopic } = {}) {
    await dispatch('finalizeMasterForExam', { masterUnitId })
    const res = await Assignment.assign({
      masterUnitId,
      subjectTopic: subjectTopic || state.subjectTopicId,
      gradeTopic: gradeTopic || state.gradeTopicId,
      chapterTitle: chapterTitle || '',
      childTitle: childTitle || '',
      courses: courses || [],
    })
    return res.data
  },

  async finalizeMasterForExam({ dispatch, rootState, rootGetters, state }, { masterUnitId } = {}) {
    if (!masterUnitId) return
    const master = (await unitRepository.fetchOne(masterUnitId)).data || {}
    const type = master.type || rootState.draftUnit.type
    if (!EXAM_UNIT_TYPES.includes(type)) {
      const effectiveSettings =
        master.draft && master.draft.settings ? master.draft.settings : master.settings || {}
      if (effectiveSettings.isDraft === false) return
      // Promote the composed title to the live unit. Homework's title otherwise lives
      // only in the draft (saved via a 3s-debounced sync-draft) and never reaches the
      // top-level `title`, so the assignment shows no name. Prefer the current draft
      // state; fall back to the saved draft info, then the existing unit title.
      const info = rootGetters['draftUnit/getInfo'] || {}
      const draftInfo = (master.draft && master.draft.info) || {}
      const title =
        (rootState.draftUnit.id === master.id ? info.title : '') || draftInfo.title || master.title || ''
      await dispatch(
        'unit/updateUnit',
        { id: master.id, title, settings: { ...effectiveSettings, isDraft: false } },
        { root: true }
      )
      return
    }
    if (master.exam) return

    if (rootState.draftUnit.id !== master.id) {
      await dispatch('draftUnit/initDraft', { unit: master }, { root: true })
    }
    await dispatch('unit/setSelectedUnit', master, { root: true })

    const detailInfo = rootGetters['draftUnit/getDataUnit'] || {}
    const settings = {
      ...rootGetters['draftUnit/getUnitSetting'],
      ...rootGetters['draftUnit/getExamSetting'],
    }
    const vendorId = master.vendorId || vendor.state.vendor.vendorId

    const questions = detailInfo.questions || []
    if (!utils.validateMark(questions, settings)) {
      throw new Error(t('$vuetify.COURSE.COURSE_UNIT_RESOURCE_SELECTION.MSG_CHECK_TOTAL_MARK_AGAIN'))
    }
    const qInvalid = utils.validateQuestion(questions, type)
    if (qInvalid && qInvalid.length) {
      throw new Error(t('$vuetify.COURSE.COURSE_UNIT_RESOURCE_SELECTION.MSG_CHECK_MARK_AGAIN', qInvalid.join(',')))
    }
    // Standalone master has no course; the exam service needs one. Use a target class course.
    const targetCourses = (master.metadata && master.metadata.target_courses) || []
    const examCourse =
      master.course ||
      (targetCourses[0] && targetCourses[0].course) ||
      (state.intendedCourses[0] && state.intendedCourses[0].course) ||
      undefined
    const exam = await dispatch(
      'exam/createExam',
      {
        course: examCourse,
        vendorId,
        type,
        settings: { ...settings, isUpdateDataExam: false },
        startTime: utils.getDateFromSetting(settings, 'start'),
        endTime: utils.getDateFromSetting(settings, 'end'),
        resources: detailInfo.resources,
      },
      { root: true }
    )
    await dispatch('draftUnitTest/updateQuestions', exam, { root: true })

    const examId = (exam && (exam._id || exam.id)) || null
    if (!examId) throw new Error(t('$vuetify.COURSE_CONTENT.ASSIGNMENT.MSG_CREATE_EXAM_FAILED'))

    const info = rootGetters['draftUnit/getInfo'] || {}
    const rules = rootGetters['draftUnit/getRules'] || {}
    await dispatch(
      'unit/updateUnit',
      {
        id: master.id,
        type,
        title: info.title,
        syllabus: info.syllabus,
        description: info.description,
        data: {
          ...rootGetters['draftUnit/getDiscusion'],
          hasMinScore: rules.hasMinScore,
          minScore: rules.minScore,
          hasAttachment: info.hasAttachment,
          hasDescription: info.hasDescription,
          isClassIn: info.isClassIn,
          chatId: info.chatId,
        },
        settings: { ...rootGetters['draftUnit/getUnitSetting'], isDraft: false },
        reviewConfig: rootGetters['draftUnit/getReviewConfig'],
        exam: examId,
        draft: {},
      },
      { root: true }
    )
  },

  // Server-side paginated: params { page (1-based), limit, ...filters }; response { items, total }.
  async fetchAssigned({ commit }, { page, limit, ...params } = {}) {
    const res = await Assignment.fetchAssigned({ status: 'assigned', page, limit, ...params })
    commit('setAssigned', res.data.items || [])
    commit('setAssignedTotal', res.data.total || 0)
    commit('setCount', res.data.total || 0)
    return res.data
  },
  async fetchDrafts({ commit }, { page, limit, ...params } = {}) {
    const res = await Assignment.fetchAssigned({ status: 'draft', page, limit, ...params })
    commit('setDrafts', res.data.items || [])
    commit('setDraftTotal', res.data.total || 0)
    return res.data
  },
  async deleteDraft(_, id) {
    const res = await Assignment.deleteDraft(id)
    return res.data
  },
  // id only → delete master + all class copies. { id, course } → un-assign one class.
  async deleteAssignment(_, payload) {
    const { id, course } = typeof payload === 'object' && payload !== null ? payload : { id: payload }
    const res = await Assignment.deleteAssignment(id, course)
    return res.data
  },
  async fetchHistory({ commit }, params = {}) {
    const res = await Assignment.fetchHistory(params)
    commit('setHistory', res.data || [])
    return res.data
  },

  // Edit one class's schedule: assign date (from*) and/or due date (to*).
  async updateSchedule({ dispatch }, { id, course, page, limit, ...schedule } = {}) {
    const res = await Assignment.updateSchedule({ id, course, ...schedule })
    await dispatch('fetchAssigned', { page, limit })
    return res.data
  },
}

const mutations = {
  setPendingCreate(state, payload) {
    state.pendingCreate = payload
  },
  setScope(state, { subjectTopicId, subjectTitle, gradeTopicId, gradeTitle }) {
    state.subjectTopicId = subjectTopicId
    state.subjectTitle = subjectTitle
    state.gradeTopicId = gradeTopicId
    state.gradeTitle = gradeTitle
  },
  setAssignmentType(state, assignmentType) {
    state.assignmentType = assignmentType
  },
  setExamFormat(state, examFormat) {
    state.examFormat = examFormat
  },
  setTitle(state, title) {
    state.title = title
  },
  setIntendedCourses(state, intendedCourses) {
    state.intendedCourses = intendedCourses || []
  },
  setMasterUnit(state, unit) {
    state.masterUnit = unit
  },
  setCourses(state, courses) {
    state.courses = courses
  },
  setAssigned(state, assigned) {
    state.assigned = assigned
  },
  setDrafts(state, drafts) {
    state.drafts = drafts
  },
  setAssignedTotal(state, total) {
    state.assignedTotal = total
  },
  setDraftTotal(state, total) {
    state.draftTotal = total
  },
  removeAssignedRow(state, id) {
    state.assigned = state.assigned.filter((u) => (u.id || u._id) !== id)
    state.assignedTotal = Math.max(0, state.assignedTotal - 1)
    state.count = state.assignedTotal
  },
  // Drop one class from a row: strip its target_courses entry. If no active entry
  // remains, drop the whole row (the master is no longer an assigned unit).
  removeAssignedCourse(state, { id, course }) {
    const item = state.assigned.find((u) => (u.id || u._id) === id)
    if (!item) return
    const meta = item.metadata || {}
    const remaining = ((meta.target_courses || []).filter(
      (c) => String(c.course) !== String(course)
    ))
    const hasActive = remaining.some((c) => c && c.status === 'active')
    if (!hasActive) {
      state.assigned = state.assigned.filter((u) => (u.id || u._id) !== id)
      state.assignedTotal = Math.max(0, state.assignedTotal - 1)
      state.count = state.assignedTotal
      return
    }
    const next = { ...item, metadata: { ...meta, target_courses: remaining } }
    state.assigned = state.assigned.map((u) => ((u.id || u._id) === id ? next : u))
  },
  removeDraftRow(state, id) {
    state.drafts = state.drafts.filter((u) => (u.id || u._id) !== id)
    state.draftTotal = Math.max(0, state.draftTotal - 1)
  },
  setHistory(state, history) {
    state.history = history
  },
  setCount(state, count) {
    state.count = count
  },
}

const getters = {
  getScope: (state) => ({
    subjectTopicId: state.subjectTopicId,
    subjectTitle: state.subjectTitle,
    gradeTopicId: state.gradeTopicId,
    gradeTitle: state.gradeTitle,
  }),
  getAssignmentType: (state) => state.assignmentType,
  getExamFormat: (state) => state.examFormat,
  getTitle: (state) => state.title,
  getIntendedCourses: (state) => state.intendedCourses,
  getMasterUnit: (state) => state.masterUnit,
  getCourses: (state) => state.courses,
  getAssigned: (state) => state.assigned,
  getDrafts: (state) => state.drafts,
  getAssignedTotal: (state) => state.assignedTotal,
  getDraftTotal: (state) => state.draftTotal,
  getHistory: (state) => state.history,
  getCount: (state) => state.count,
}

export default {
  namespaced,
  state,
  actions,
  mutations,
  getters,
}
